import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
  STATUS_OPTIONS,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import Roles from "../../../models/roles";
import {
  IPageLink,
  TAdminUser,
  TAdminUserErrorResponse,
  TCreateAdminFormValues,
} from "../../../models/superadmin";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  createAdminUsersThunk,
  getAdminUsersThunk,
  updateAdminUserDetailThunk
} from "../../../store/superadmin/thunks";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { capitalizeName } from "../../../utils/stringUtils";
import SaveNotification from "../../SaveNotification";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedPlus from "../../UI/Icons/RoundedPlus";
import Search from "../../UI/Icons/Search";
import SortBySelect from "../../UI/Inputs/SortBySelect";
import TextInput from "../../UI/Inputs/TextInput";
import PopupModal from "../../UI/Popup";
import UserForm from "./UserForm";
import UserList from "./UserList";

const User: FC = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedAdminDetail, setSelectedAdminDetail] =
    useState<TAdminUser | null>(null);
  const isInitialLoad = useRef<boolean>(false);
  const [superAdmincurrentPage, setSuperAdminCurrentPage] = useState<number>(1);
  const [superAdmintotalPages, setSuperAdminTotalPages] = useState<number>(1);
  const [superAdminpaginationLinks, setSuperAdminPaginationLinks] = useState<
    IPageLink[]
  >([]);
  const [superAdminsPerPageCount, setSuperAdminsPerPageCount] =
    useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [generalAdmincurrentPage, setGeneralAdminCurrentPage] =
    useState<number>(1);
  const [generalAdmintotalPages, setGeneralAdminTotalPages] =
    useState<number>(1);
  const [generalAdminpaginationLinks, setGeneralAdminPaginationLinks] =
    useState<IPageLink[]>([]);
  const [generalAdminPerPageList, setGeneralAdminPerPageList] =
    useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [searchInput, setSearchInput] = useState<string>(""); // captures typing input
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("active");
  const adminUserList = useSelector((state) => state.superadmin.adminUserList);
  // const [centresList, setCentresList] = useState<Array<any>>([]);
  const allCentersList = useSelector((state) => state.Centres.allCenters);
  const getAllActiveCentres = useSelector(
    (state) => state.superadmin.allActiveCentres
  );

  const user = useSelector((state) => state.auth.user);

  /** Submit the form for adding the admin */
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    setErrors,
    handleChange,
    resetForm,
  } = useFormik({
    validationSchema: createValidationSchema("adminUser"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      candidate_id: 0,
      role_id: "",
      first_name: "",
      last_name: "",
      phone_number: "",
      email: "",
      timesheet_access: 0,
      centre_ids: [],
      has_all_centre_access: 0,
    },
    onSubmit: async (values: TCreateAdminFormValues) => {
      const payload = {
        ...values,
        first_name: capitalizeName(values.first_name),
        last_name: capitalizeName(values.last_name),
      };
      try {
        const result =
          values.candidate_id !== 0
            ? await dispatch(updateAdminUserDetailThunk(payload))
            : await dispatch(createAdminUsersThunk(payload));

        if (isFulfilled(result)) {
          // getAllUsers();
          setShowModal(false);
          setShowNotification(true)
          setTimeout(async() => {
            await getAllUsers()
          }, 3000)
        } else {
          const res = result.payload as TAdminUserErrorResponse;
          if (res?.errors) {
            const formattedErrors: Record<string, string> = {};
            Object.entries(res.errors).forEach(([field, messages]) => {
              if (messages && messages.length > 0) {
                formattedErrors[field] = messages[0];
              }
            });
            setErrors(formattedErrors);
          }
        }
      } catch (error) {
        console.error("Submission error", error);
      }
    },
  });



  useEffect(() => {
    if (adminUserList?.super_admins) {
      setSuperAdminTotalPages(adminUserList?.super_admins?.last_page || 1);
      setSuperAdminPaginationLinks(adminUserList?.super_admins.links);
    }

    if (adminUserList?.general_admins) {
      setGeneralAdminTotalPages(adminUserList?.general_admins?.last_page || 1);
      setGeneralAdminPaginationLinks(adminUserList?.general_admins.links);
    }
  }, [adminUserList]);

  // Trigger API only when debounced value updates
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch);
      setSuperAdminCurrentPage(1);
      setGeneralAdminCurrentPage(1);
    }
  }, [debouncedSearch]);

  // useEffect(() => {
  //   const fetchActiveCentresList = getAllActiveCentres?.filter(
  //     (centre) => centre.business_name
  //   );
  //   if (fetchActiveCentresList) {
  //     setCentresList(fetchActiveCentresList);
  //   } else {
  //     setCentresList([]);
  //   }
  // }, [setCentresList, allCentersList, getAllActiveCentres]);

  const handleSetValue = useCallback(() => {
    const check = selectedAdminDetail !== null;
    setFieldValue('has_all_centre_access', check ? selectedAdminDetail.has_all_centre_access : 0)
    setFieldValue("candidate_id", check ? selectedAdminDetail.id : 0);
    setFieldValue("role_id", check ? selectedAdminDetail.role_id : "");
    setFieldValue("first_name", check ? capitalizeName(selectedAdminDetail.first_name) : "");
    setFieldValue("last_name", check ? capitalizeName(selectedAdminDetail.last_name) : "");
    setFieldValue(
      "phone_number",
      check ? selectedAdminDetail.phone_number : ""
    );
    setFieldValue("email", check ? selectedAdminDetail.email : "");

    // Commented out for future use
    
    // New: Conditionally set General Admin fields
    // setFieldValue(
    //   "has_all_centre_access",
    //   check && selectedAdminDetail.has_all_centre_access !== undefined
    //     ? selectedAdminDetail.has_all_centre_access
    //     : 0
    // );

    // setFieldValue(
    //   "timesheet_access",
    //   check && selectedAdminDetail.timesheet_access !== undefined
    //     ? selectedAdminDetail.timesheet_access
    //     : 0
    // );

    setFieldValue('timesheet_access', check ? selectedAdminDetail.timesheet_access : 0)

    setFieldValue(
      "centre_ids",
      check && Array.isArray(selectedAdminDetail.centre_ids)
        ? selectedAdminDetail.centre_ids
        : []
    );
  }, [selectedAdminDetail, setFieldValue]);

  useEffect(() => {
    handleSetValue();
  }, [selectedAdminDetail, handleSetValue]);

  const getAllUsers = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAdminUsersThunk({
        search: selectedKeyword,
        super_admin_page: superAdmincurrentPage,
        general_admin_page: generalAdmincurrentPage,
        super_admins_per_page: superAdminsPerPageCount,
        general_admins_per_page: generalAdminPerPageList,
        status: selectedStatus
      })
    );
    dispatch(hideTransparentPreloaderAction());
  }, [
    dispatch,
    selectedKeyword,
    superAdmincurrentPage,
    generalAdmincurrentPage,
    superAdminsPerPageCount,
    generalAdminPerPageList,
    selectedStatus,
  ]);

  useEffect(() => {
    getAllUsers();
  }, [
    getAllUsers,
    selectedKeyword,
    superAdmincurrentPage,
    generalAdmincurrentPage,
    superAdminsPerPageCount,
    generalAdminPerPageList,
    selectedStatus,
  ]);

  const onClickClose = () => {
    setShowModal(false);
    setSelectedAdminDetail(null);
  };

  const handleChooseAdminType = (value: string) => {
    setFieldValue("role_id", parseInt(value));
    if (value === "1") {
      // Reset fields not relevant to Super Admin
      setFieldValue("centre_ids", []);
      setFieldValue("has_all_centre_access", 0);
      setFieldValue("timesheet_access", 0);
    }
  };

  // const fetchCentres = async () => {
  //   dispatch(showTransparentPreloaderAction());
  //   await dispatch(getAllActiveCentresThunk({ paginate: false }));
  //   dispatch(hideTransparentPreloaderAction());
  // };

  const handleCreateAdmin = async () => {
    // await fetchCentres();
    setSelectedAdminDetail(null);
    setShowModal(!false);
    setErrors({});
    resetForm(); // resets values and errors
  };

  const handleEdit = async (id: number | null, type: string) => {
    // await fetchCentres();
    setOpenDropdown(false);
    setErrors({});
    resetForm(); // clear previous state

    const selectedAdminUserDetails =
      type === "superAdmin"
        ? adminUserList?.super_admins?.data.find(
            (superAdminUser: TAdminUser) => superAdminUser.id === id
          )
        : adminUserList?.general_admins?.data.find(
            (generalAdminUser: TAdminUser) => generalAdminUser.id === id
          );
    setSelectedAdminDetail(selectedAdminUserDetails || null);
    setShowModal(!false);
  };

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handleSuperAdminPageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("super_admin_page");
      setSuperAdminCurrentPage(Number(page));
    }
  };

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handleGeneralAdminPageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("general_admin_page");
      setGeneralAdminCurrentPage(Number(page));
    }
  };

  /**
   * Handles the change in selected tags and updates the form field value for "centre_ids".
   *
   * @param centreTagId - An array of id representing the selected tags.
   *               Each tag corresponds to the `id` of a center.
   *
   * The function maps the selected tags to their corresponding center IDs
   * by finding the matching center in the `allCentersList` based on the `id`.
   * It then updates the form field "centre_ids" with the array of selected center IDs.
   */
  const handleTagsChange = (centreTagId: number | Array<number>) => {
    let selectedTagValue: number[] = [];
    if (Array.isArray(centreTagId) && centreTagId.length > 0) {
      selectedTagValue = centreTagId
        .map((id) => Number(id))
        .filter((id) => !isNaN(id));
    }
    setFieldValue("centre_ids", selectedTagValue);
  };

  const handleTimesheetChange = (checked: boolean) => {
    setFieldValue("timesheet_access", checked ? 1 : 0);
  };

  const handleAccessToAllCentresChange = (checked: boolean) => {
    setFieldValue("has_all_centre_access", checked ? 1 : 0);
  };

  const isSuperAdmin = useMemo(
    () => user?.role.name === Roles.SUPER_ADMIN,
    [user]
  );

  const handleFilterStatus = (selectedOption: any) => {};

  const handleSelectFilter = (selectedOption: any) => {
    setSelectedStatus(selectedOption.value);
    setSuperAdminCurrentPage(1);
    setGeneralAdminCurrentPage(1);
  }

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={`${values.candidate_id !== 0 ? "Saved" : "Invite Sent"}`}
          onClose={() => setShowNotification(false)}
        />
      )}
      <PopupModal 
        visible={showModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
      >
        <UserForm
          touched={touched}
          values={values}
          errors={errors}
          handleChooseAdminType={handleChooseAdminType}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          // centresList={centresList}
          handleTagsChange={handleTagsChange}
          handleAccessToAllCentresChange={handleAccessToAllCentresChange}
          handleTimesheetChange={handleTimesheetChange}
          selectedAdminDetail={selectedAdminDetail}
          setShowModal={setShowModal}
          getAllUsers={getAllUsers}
        />
      </PopupModal>
      <div className="d-flex flex-column gap-20">
        <div className="d-flex gap-10 align-items-center justify-content-between">

          <div className="d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-lg-center">
            <div className="form-group w-200px input-icon icon-left">
              <Search />
              <TextInput
                field={"search"}
                label={"Search..."}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={" " + ""}
              />
            </div>
            <div className="d-flex gap-3 align-items-center position-relative">
                <div className="fs-13 text-black text-nowrap">Status</div>
                <div className="form-group">
                  <SortBySelect
                    options={STATUS_OPTIONS}
                    placeholder="Select..."
                    enableFilter={false}
                    defaultValue={STATUS_OPTIONS[1]}
                    onChange={handleFilterStatus}
                    onSelect={handleSelectFilter}
                  />
                </div>
            </div>
          </div>

          <div className="d-flex">
            {isSuperAdmin ? (
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-white text-black"
                onClick={handleCreateAdmin}
              >
                <RoundedPlus fill={"#000000"} />
                Create
              </CustomButton>
            ) : (
              ""
            )}
          </div>
        </div>
        <UserList
          adminUserList={adminUserList}
          searchInput={searchInput}
          superAdmincurrentPage={superAdmincurrentPage}
          superAdmintotalPages={superAdmintotalPages}
          superAdminpaginationLinks={superAdminpaginationLinks}
          setSuperAdminPerPageList={setSuperAdminsPerPageCount}
          setSuperAdminCurrentPage={setSuperAdminCurrentPage}
          handleSuperAdminPageChange={handleSuperAdminPageChange}
          setGeneralAdminPerPageList={setGeneralAdminPerPageList}
          setGeneralAdminCurrentPage={setGeneralAdminCurrentPage}
          generalAdmincurrentPage={generalAdmincurrentPage}
          generalAdmintotalPages={generalAdmintotalPages}
          generalAdminpaginationLinks={generalAdminpaginationLinks}
          handleGeneralAdminPageChange={handleGeneralAdminPageChange}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          handleEdit={handleEdit}
          getAllUsers={getAllUsers}
        />
      </div>
    </>
  );
};

export default User;
function setSelectedStatus(value: any) {
  throw new Error("Function not implemented.");
}

